import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import api from '../utils/api';
import { toast } from '../hooks/use-toast';
import { 
  CheckCircle, Clock, FileText, Download, Copy, 
  ChevronRight, GraduationCap, Calendar, Loader2,
  PartyPopper, ExternalLink, AlertCircle
} from 'lucide-react';

const SubmissionConfirmation = () => {
  const { applicationId } = useParams();
  const navigate = useNavigate();
  const [confirmation, setConfirmation] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (applicationId) {
      loadConfirmation();
    }
  }, [applicationId]);

  const loadConfirmation = async () => {
    try {
      const [confirmRes, statusRes] = await Promise.all([
        api.get(`/submission/confirmation/${applicationId}`),
        api.get(`/submission/status/${applicationId}`)
      ]);
      setConfirmation(confirmRes.data.confirmation);
      setStatus(statusRes.data);
    } catch (error) {
      // Global error handler shows toast
    } finally {
      setLoading(false);
    }
  };

  const copyConfirmationNumber = () => {
    if (confirmation?.confirmation_number) {
      navigator.clipboard.writeText(confirmation.confirmation_number);
      toast({ title: 'Copied!', description: 'Confirmation number copied to clipboard' });
    }
  };

  const handleWithdraw = async () => {
    if (!window.confirm('Are you sure you want to withdraw this application? This action cannot be undone.')) {
      return;
    }

    try {
      await api.post(`/submission/withdraw/${applicationId}`, {});
      toast({ title: 'Withdrawn', description: 'Application has been withdrawn' });
      navigate('/decisions');
    } catch (error) {
      // Global error handler shows toast
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (!confirmation) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="py-8 text-center">
            <AlertCircle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">No Confirmation Found</h2>
            <p className="text-gray-600 mb-4">This application has not been submitted yet.</p>
            <Button onClick={() => navigate(`/application/${applicationId}/review`)}>
              Go to Application Review
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Success Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
            <CheckCircle className="h-10 w-10 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Application Submitted!</h1>
          <p className="text-gray-600">Your application has been successfully received</p>
        </div>

        {/* Confirmation Details */}
        <Card className="mb-6">
          <CardHeader className="bg-emerald-50 border-b border-emerald-100">
            <CardTitle className="flex items-center gap-2">
              <GraduationCap className="h-5 w-5 text-emerald-600" />
              {confirmation.college_name}
            </CardTitle>
            <CardDescription>{confirmation.college_location}</CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            {/* Confirmation Number */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Confirmation Number</p>
                  <p className="font-mono text-xl font-bold text-emerald-700">
                    {confirmation.confirmation_number}
                  </p>
                </div>
                <Button variant="outline" size="sm" onClick={copyConfirmationNumber}>
                  <Copy className="h-4 w-4 mr-2" />
                  Copy
                </Button>
              </div>
            </div>

            {/* Details Grid */}
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-500">Applicant</p>
                <p className="font-medium">{confirmation.applicant_name}</p>
              </div>
              <div>
                <p className="text-gray-500">Email</p>
                <p className="font-medium">{confirmation.applicant_email}</p>
              </div>
              <div>
                <p className="text-gray-500">Application Type</p>
                <Badge variant="secondary">{confirmation.application_type}</Badge>
              </div>
              <div>
                <p className="text-gray-500">Submitted</p>
                <p className="font-medium">
                  {confirmation.submitted_at && new Date(confirmation.submitted_at).toLocaleDateString()}
                </p>
              </div>
            </div>

            <Separator className="my-6" />

            {/* Status */}
            <div className="flex items-center gap-3">
              <CheckCircle className="h-5 w-5 text-green-600" />
              <span className="font-medium text-green-700">{confirmation.status}</span>
            </div>
            
            <p className="text-sm text-gray-500 mt-4 italic">
              {confirmation.note}
            </p>
          </CardContent>
        </Card>

        {/* Timeline */}
        {status?.timeline && status.timeline.length > 0 && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-lg">Submission Timeline</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {status.timeline.map((event, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <div className="mt-1">
                      <div className="w-3 h-3 rounded-full bg-emerald-500" />
                    </div>
                    <div>
                      <p className="font-medium capitalize">{event.status.replace(/_/g, ' ')}</p>
                      <p className="text-sm text-gray-500">
                        {new Date(event.timestamp).toLocaleString()}
                      </p>
                      {event.message && (
                        <p className="text-sm text-gray-600 mt-1">{event.message}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Actions */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">What&apos;s Next?</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 mb-6">
              <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                <ChevronRight className="h-4 w-4 text-blue-600" />
                <span className="text-sm text-blue-800">Check your email for a confirmation receipt</span>
              </div>
              <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                <ChevronRight className="h-4 w-4 text-blue-600" />
                <span className="text-sm text-blue-800">Track your application status in the Decisions tab</span>
              </div>
              <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                <ChevronRight className="h-4 w-4 text-blue-600" />
                <span className="text-sm text-blue-800">Ensure your recommenders have submitted their letters</span>
              </div>
              <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                <ChevronRight className="h-4 w-4 text-blue-600" />
                <span className="text-sm text-blue-800">Request official score reports if required</span>
              </div>
            </div>

            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => navigate('/decisions')}>
                <Calendar className="h-4 w-4 mr-2" />
                Track Decisions
              </Button>
              <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700" onClick={() => navigate('/dashboard')}>
                Go to Dashboard
              </Button>
            </div>

            {status?.status !== 'withdrawn' && status?.decision_status === 'pending' && (
              <div className="mt-6 pt-4 border-t">
                <Button 
                  variant="ghost" 
                  className="text-red-600 hover:text-red-700 hover:bg-red-50 w-full"
                  onClick={handleWithdraw}
                >
                  Withdraw Application
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SubmissionConfirmation;
